import { Skeleton } from "@/components/ui/skeleton";

export function PageLoadingSkeleton() {
    return (
        <div className="animate-pulse">
            {/* Hero/Headline Section Skeleton */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 max-w-[90%]">
                <div className="w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-lg shadow-xl overflow-hidden">
                    <div className="px-4 pt-4 pb-3 md:px-5 md:pt-5 md:pb-4">
                        <div className="max-w-7xl mx-auto">
                            {/* Date badge */}
                            <Skeleton className="h-4 w-32 mb-3 bg-slate-700/50" />
                            {/* Headline */}
                            <Skeleton className="h-8 w-3/4 mb-4 bg-slate-700/50" />
                            {/* Subtitle text area */}
                            <div className="space-y-2 mb-3">
                                <Skeleton className="h-4 w-full bg-slate-700/50" />
                                <Skeleton className="h-4 w-5/6 bg-slate-700/50" />
                                <Skeleton className="h-4 w-4/5 bg-slate-700/50" />
                            </div>
                            {/* Navigation dots */}
                            <div className="flex justify-center gap-2 mt-2">
                                <Skeleton className="h-2 w-2 rounded-full bg-slate-600" />
                                <Skeleton className="h-2 w-2 rounded-full bg-slate-700/50" />
                                <Skeleton className="h-2 w-2 rounded-full bg-slate-700/50" />
                                <Skeleton className="h-2 w-2 rounded-full bg-slate-700/50" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sentiment Section Skeleton */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 max-w-[90%]">
                {/* Header with toggle skeleton */}
                <div className="flex items-center gap-0 mb-6">
                    <Skeleton className="h-7 w-28 mr-6 bg-slate-700/50" />
                    <Skeleton className="h-10 w-56 rounded-lg bg-slate-800/50" />
                </div>
                {/* Sentiment content skeleton */}
                <div className="w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-lg shadow-xl">
                    <div className="px-4 pt-5 pb-4 md:px-6 md:pt-6 md:pb-5">
                        <div className="flex gap-8 items-center">
                            {/* Left side circle */}
                            <div className="flex flex-col items-center gap-3 flex-shrink-0">
                                <Skeleton className="h-[140px] w-[140px] md:h-[160px] md:w-[160px] rounded-full bg-slate-700/50" />
                                <Skeleton className="h-3 w-20 bg-slate-700/50" />
                            </div>
                            <div className="w-px h-28 bg-slate-700/30"></div>
                            {/* Right side content */}
                            <div className="flex-1 space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <Skeleton className="h-6 w-20 rounded-md bg-slate-700/50" />
                                        <Skeleton className="h-10 flex-1 rounded-md bg-slate-700/50" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Section Skeleton */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 max-w-[90%]">
                {/* Header with toggle skeleton */}
                <div className="flex items-center gap-0 mb-6">
                    <Skeleton className="h-7 w-20 mr-6 bg-slate-700/50" />
                    <Skeleton className="h-10 w-44 rounded-lg bg-slate-800/50" />
                </div>
                {/* Bulletin content skeleton */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-6 md:p-8">
                    <Skeleton className="h-7 w-40 mb-6 bg-slate-700/50" />
                    <div className="pl-4 border-l-2 border-slate-700/50 space-y-6">
                        {[1, 2].map((i) => (
                            <div key={i} className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <Skeleton className="h-5 w-20 bg-slate-700/50" />
                                    <Skeleton className="h-5 w-28 rounded-full bg-slate-700/50" />
                                </div>
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full bg-slate-700/50" />
                                    <Skeleton className="h-4 w-5/6 bg-slate-700/50" />
                                    <Skeleton className="h-4 w-4/5 bg-slate-700/50" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function HeadlineSkeleton() {
    return (
        <div className="w-full text-white px-3 pt-5 pb-3 md:px-4 md:pt-6 md:pb-4 lg:px-5 lg:pt-7 lg:pb-5 relative min-h-[220px]">
            <div className="max-w-7xl mx-auto pt-2">
                <div className="mb-4">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-10 w-3/4 mb-4" />
                </div>
                <div className="space-y-2 mb-5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                </div>
                <div className="flex justify-center mb-5">
                    <Skeleton className="h-10 w-10 rounded-full" />
                </div>
            </div>
        </div>
    );
}

export function SentimentSkeleton() {
    return (
        <div className="w-full text-white px-3 pt-5 pb-3 md:px-4 md:pt-6 md:pb-4 lg:px-5 lg:pt-7 lg:pb-5 relative">
            <div className="max-w-6xl mx-auto pt-2">
                <div className="flex gap-8 items-center">
                    {/* Left side circle */}
                    <div className="flex flex-col items-center gap-3 flex-shrink-0">
                        <Skeleton className="h-[180px] w-[180px] rounded-full" />
                        <Skeleton className="h-3 w-24" />
                    </div>

                    <div className="w-px h-32 bg-slate-700"></div>

                    {/* Right side content */}
                    <div className="flex-1 space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-start gap-3">
                                <Skeleton className="h-8 w-24 rounded-md" />
                                <Skeleton className="h-12 flex-1 rounded-md" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function BulletinSkeleton() {
    return (
        <div className="animate-pulse">
            <section className="mb-12">
                <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-8">
                    <div className="mb-8">
                        <Skeleton className="h-8 w-48 mb-6" />
                        <div className="pl-4 border-l-2 border-slate-700 space-y-8">
                            {[1, 2].map((i) => (
                                <div key={i} className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-6 w-24" />
                                        <Skeleton className="h-6 w-32 rounded-full" />
                                    </div>
                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-5/6" />
                                        <Skeleton className="h-4 w-4/5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
